<script setup lang="ts">
import { SentenceGroupCategoryType } from 'types/types'
import { reactive, watch } from 'vue'

import store from '@/store'

const state = reactive({
  dragFromIndex: null as number | null,
  editingDescription: '',
  editingId: null as string | null,
  localCategories: [] as SentenceGroupCategoryType[],
  saving: false,
})

function syncFromStore() {
  state.localCategories = [...store.getters.teacher.sentenceGroupCategories]
}

watch(
  () => store.getters.teacher.sentenceGroupCategories,
  () => {
    if (!state.saving && state.editingId === null) {
      syncFromStore()
    }
  },
  { immediate: true },
)

function reset() {
  syncFromStore()
  state.editingId = null
  state.editingDescription = ''
  state.dragFromIndex = null
}

function startEdit(cat: SentenceGroupCategoryType) {
  state.editingId = cat.id
  state.editingDescription = cat.description
}

function cancelEdit() {
  state.editingId = null
  state.editingDescription = ''
}

async function saveEdit(cat: SentenceGroupCategoryType) {
  const description = state.editingDescription.trim()
  if (!description) {
    return
  }
  state.saving = true
  try {
    if (!(await store.dispatch.teacher.createOrUpdateSentenceGroupCategory({ description, id: cat.id }))) {
      return
    }
    const i = state.localCategories.findIndex((c) => c.id === cat.id)
    if (i !== -1) {
      state.localCategories[i] = { ...state.localCategories[i], description }
    }
    state.editingId = null
    state.editingDescription = ''
  } finally {
    state.saving = false
  }
}

const ADDING_ID = '_new'

function startAdd() {
  state.editingId = ADDING_ID
  state.editingDescription = ''
}

async function saveAdd() {
  const description = state.editingDescription.trim()
  if (!description) {
    return
  }
  const nextOrder = state.localCategories.length ? Math.max(...state.localCategories.map((c) => c.order)) + 1 : 1
  state.saving = true
  try {
    if (!(await store.dispatch.teacher.createOrUpdateSentenceGroupCategory({ description, order: nextOrder }))) {
      return
    }
    syncFromStore()
    state.editingId = null
    state.editingDescription = ''
  } finally {
    state.saving = false
  }
}

function confirmDelete(cat: SentenceGroupCategoryType) {
  const count = store.getters.teacher.sentenceGroups.filter((sg) => sg.category?.id === cat.id).length
  const message = count
    ? `Delete "${cat.description}"? ${count} text${count === 1 ? '' : 's'} will become uncategorized.`
    : `Delete "${cat.description}"?`
  store.dispatch.dialog.show({
    acceptColor: 'error',
    acceptText: 'Delete',
    isRejectable: true,
    message,
    onAccept: () => doDelete(cat),
    title: 'Delete category?',
  })
}

async function doDelete(cat: SentenceGroupCategoryType) {
  state.saving = true
  try {
    if (await store.dispatch.teacher.deleteSentenceGroupCategory({ id: cat.id })) {
      state.localCategories = state.localCategories.filter((c) => c.id !== cat.id)
    }
  } finally {
    state.saving = false
  }
}

function onDragStart(index: number) {
  state.dragFromIndex = index
}

async function onDrop(toIndex: number) {
  if (state.dragFromIndex === null || state.dragFromIndex === toIndex) {
    state.dragFromIndex = null
    return
  }
  const moved = state.localCategories.splice(state.dragFromIndex, 1)[0]
  state.localCategories.splice(toIndex, 0, moved)
  state.dragFromIndex = null
  await saveOrders()
}

async function saveOrders() {
  state.saving = true
  try {
    await Promise.all(
      state.localCategories.map((cat, i) =>
        store.dispatch.teacher.createOrUpdateSentenceGroupCategory({ description: cat.description, id: cat.id, order: i + 1 }),
      ),
    )
    syncFromStore()
  } finally {
    state.saving = false
  }
}

defineExpose({ reset })
</script>

<template>
  <div style="max-width: 500px">
    <v-list>
      <v-list-item
        v-for="(cat, index) in state.localCategories"
        :key="cat.id"
        :draggable="state.editingId === null"
        @dragover.prevent
        @dragstart="onDragStart(index)"
        @drop.prevent="onDrop(index)"
      >
        <template #prepend>
          <v-icon class="drag-handle" icon="mdi-drag" style="cursor: grab" />
        </template>

        <template v-if="state.editingId === cat.id">
          <v-text-field
            v-model="state.editingDescription"
            autofocus
            density="compact"
            hide-details
            variant="underlined"
            @keydown.enter="saveEdit(cat)"
            @keydown.esc="cancelEdit"
          />
        </template>
        <template v-else>
          <v-list-item-title>{{ cat.description }}</v-list-item-title>
        </template>

        <template #append>
          <template v-if="state.editingId === cat.id">
            <v-btn
              :disabled="state.saving || !state.editingDescription.trim()"
              icon="mdi-check"
              size="small"
              variant="text"
              @click="saveEdit(cat)"
            />
            <v-btn :disabled="state.saving" icon="mdi-close" size="small" variant="text" @click="cancelEdit" />
          </template>
          <template v-else>
            <v-btn :disabled="state.saving" icon="mdi-pencil" size="small" variant="text" @click="startEdit(cat)" />
            <v-btn :disabled="state.saving" icon="mdi-delete" size="small" variant="text" @click="confirmDelete(cat)" />
          </template>
        </template>
      </v-list-item>
      <v-list-item v-if="state.editingId === ADDING_ID">
        <v-text-field
          v-model="state.editingDescription"
          autofocus
          density="compact"
          hide-details
          placeholder="Category name"
          variant="underlined"
          @keydown.enter="saveAdd"
          @keydown.esc="cancelEdit"
        />
        <template #append>
          <v-btn
            :disabled="state.saving || !state.editingDescription.trim()"
            icon="mdi-check"
            size="small"
            variant="text"
            @click="saveAdd"
          />
          <v-btn :disabled="state.saving" icon="mdi-close" size="small" variant="text" @click="cancelEdit" />
        </template>
      </v-list-item>
    </v-list>
    <p v-if="state.localCategories.length === 0 && state.editingId !== ADDING_ID" class="text-medium-emphasis">No categories.</p>

    <v-btn class="mt-2" color="primary" :disabled="state.editingId === ADDING_ID" @click="startAdd"> Add category </v-btn>
  </div>
</template>
